import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, ShoppingBag, Euro, Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatEUR } from "@/lib/api";
import { invalidateSettingsCache } from "@/lib/settings";
import { invalidateContentCache } from "@/lib/content";
import { getSheetInfo } from "@/lib/materials";

const CONTENT_GROUPS = [
  { title: "Domača stran — uvod", desc: "Veliki naslov in uvodno besedilo ter tri slike ob njem.", fields: [
    ["hero_naslov", "Glavni naslov", "input"],
    ["hero_podnaslov", "Podnaslov", "text"],
    ["hero_slika_1", "Slika 1 — velika (URL)", "input"],
    ["hero_slika_2", "Slika 2 — mala (URL)", "input"],
    ["hero_slika_3", "Slika 3 — mala (URL)", "input"],
  ]},
  { title: "O nas", desc: "Odsek »O nas« na domači strani.", fields: [
    ["onas_naslov", "Naslov", "input"],
    ["onas_besedilo", "Besedilo", "text"],
    ["onas_slika", "Slika (URL)", "input"],
  ]},
  { title: "Kategorije", desc: "Opisi in slike kategorijskih kartic na domači strani.", fields: [
    ["kat_iverali_opis", "Iverali — opis", "input"],
    ["kat_iverali_slika", "Iverali — slika (URL)", "input"],
    ["kat_delovne_plosce_opis", "Delovne plošče — opis", "input"],
    ["kat_delovne_plosce_slika", "Delovne plošče — slika (URL)", "input"],
    ["kat_kuhinjski_elementi_opis", "Kuhinjski elementi — opis", "input"],
    ["kat_kuhinjski_elementi_slika", "Kuhinjski elementi — slika (URL)", "input"],
    ["kat_pohistveno_okovje_opis", "Pohištveno okovje — opis", "input"],
    ["kat_pohistveno_okovje_slika", "Pohištveno okovje — slika (URL)", "input"],
    ["kat_razrez_opis", "Razrez — opis", "input"],
    ["kat_razrez_slika", "Razrez — slika (URL)", "input"],
  ]},
  { title: "Pravna besedila", desc: "Vsebina pravnih strani. Prazna vrstica = nov odstavek; vrstica, ki se začne z »## «, je podnaslov; »- « je alineja.", fields: [
    ["pogoji_text", "Pogoji poslovanja", "textarea-lg"],
    ["piskotki_text", "Pravilnik o piškotkih", "textarea-lg"],
    ["pravna_text", "Pravna obvestila", "textarea-lg"],
  ]},
];
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageField from "@/components/ImageField";

const STATUSES = ["prejeto", "v obdelavi", "odposlano", "zaključeno", "preklicano"];
const CATEGORIES = [
  { id: "iverali", label: "Lesni iverali in plošče" },
  { id: "delovne-plosce", label: "Delovne plošče" },
  { id: "kuhinjski-elementi", label: "Kuhinjski elementi" },
  { id: "pohistveno-okovje", label: "Pohištveno okovje" },
];
const EMPTY_PRODUCT = { name: "", category: "iverali", price: "", old_price: "", unit: "kos", decor: "", thickness: "", dimensions: "", image: "", badge: "", desc: "" };

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("hc-admin-token")}` });

export default function AdminPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pregled");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [contentForm, setContentForm] = useState(null);
  const [savingContent, setSavingContent] = useState(false);

  const handle401 = useCallback((err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem("hc-admin-token");
      navigate("/admin/prijava");
      return true;
    }
    return false;
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, o, p, st, c] = await Promise.all([
        api.get("/admin/stats", { headers: authHeaders() }),
        api.get("/admin/orders", { headers: authHeaders() }),
        api.get("/products"),
        api.get("/settings"),
        api.get("/content"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
      setSettingsForm(st.data);
      setContentForm(c.data);
    } catch (err) {
      if (!handle401(err)) toast.error("Napaka pri nalaganju podatkov");
    }
  }, [handle401]);

  useEffect(() => {
    if (!localStorage.getItem("hc-admin-token")) {
      navigate("/admin/prijava");
      return;
    }
    loadAll();
  }, [navigate, loadAll]);

  const logout = () => {
    localStorage.removeItem("hc-admin-token");
    navigate("/admin/prijava");
  };

  const changeStatus = async (orderId, status) => {
    try {
      const res = await api.patch(`/admin/orders/${orderId}`, { status }, { headers: authHeaders() });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? res.data : o)));
      toast.success(`Status naročila ${orderId}: ${status}`);
    } catch (err) {
      if (!handle401(err)) toast.error("Statusa ni bilo mogoče spremeniti");
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, category: p.category, price: p.price,
      old_price: p.old_price ?? "", unit: p.unit, decor: p.decor ?? "",
      thickness: p.thickness ?? "", dimensions: p.dimensions ?? "",
      image: p.image ?? "", badge: p.badge ?? "", desc: p.desc ?? "",
    });
    setDialogOpen(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      old_price: form.old_price === "" ? null : Number(form.old_price),
      thickness: form.thickness === "" ? null : Number(form.thickness),
      decor: form.decor || null,
      badge: form.badge || null,
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing}`, payload, { headers: authHeaders() });
        toast.success("Izdelek posodobljen");
      } else {
        await api.post("/admin/products", payload, { headers: authHeaders() });
        toast.success("Izdelek dodan");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      if (!handle401(err)) toast.error("Shranjevanje ni uspelo");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Res želite izbrisati izdelek „${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, { headers: authHeaders() });
      toast.success("Izdelek izbrisan");
      loadAll();
    } catch (err) {
      if (!handle401(err)) toast.error("Brisanje ni uspelo");
    }
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/content", contentForm, { headers: authHeaders() });
      invalidateContentCache();
      toast.success("Vsebina shranjena — osvežite stran trgovine");
    } catch (err) {
      if (!handle401(err)) toast.error("Shranjevanje ni uspelo");
    } finally {
      setSavingContent(false);
    }
  };

  const catLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

  const pad4 = (arr) => {
    const a = [...(arr || [])];
    while (a.length < 4) a.push({ oddelek: "", stevilka: "" });
    return a.slice(0, 4);
  };

  const updatePhone = (i, field, value) => {
    const current = settingsForm.telefoni?.length
      ? pad4(settingsForm.telefoni)
      : pad4([{ oddelek: "", stevilka: settingsForm.telefon || "" }]);
    current[i] = { ...current[i], [field]: value };
    setSettingsForm({ ...settingsForm, telefoni: current });
  };

  const phoneRows = settingsForm
    ? settingsForm.telefoni?.length
      ? pad4(settingsForm.telefoni)
      : pad4([{ oddelek: "", stevilka: settingsForm.telefon || "" }])
    : [];

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settingsForm, { headers: authHeaders() });
      invalidateSettingsCache();
      toast.success("Kontaktni podatki shranjeni — osvežite stran trgovine");
    } catch (err) {
      if (!handle401(err)) toast.error("Shranjevanje ni uspelo");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-tech text-xs uppercase tracking-[0.2em] text-[#B45309] mb-1">Admin</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1D1A]">Nadzorna plošča</h1>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="flex items-center gap-2 h-10 px-5 rounded-full border border-[#E4E0D7] text-sm font-semibold text-stone-600 hover:text-red-600 hover:border-red-300 transition-colors"
        >
          <LogOut className="w-4 h-4" /> Odjava
        </button>
      </div>

      <div className="flex gap-2 mb-8">
        {[["pregled", "Pregled"], ["narocila", "Naročila"], ["izdelki", "Izdelki"], ["nastavitve", "Nastavitve"], ["vsebina", "Vsebina"]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
              tab === id ? "bg-[#2D4030] text-[#FBF9F5]" : "bg-white border border-[#E4E0D7] text-stone-600 hover:border-[#2D4030]/50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "pregled" && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="admin-stats">
          {[
            { icon: ShoppingBag, label: "Št. naročil", value: stats.orders, testid: "stat-orders" },
            { icon: Euro, label: "Promet (brez preklicanih)", value: formatEUR(stats.revenue), testid: "stat-revenue" },
            { icon: Package, label: "Izdelkov v katalogu", value: stats.products, testid: "stat-products" },
          ].map((s) => (
            <div key={s.testid} data-testid={s.testid} className="bg-white rounded-2xl border border-[#E4E0D7] p-6">
              <s.icon className="w-6 h-6 text-[#B45309] mb-3" />
              <p className="font-tech font-bold text-2xl text-[#1A1D1A]">{s.value}</p>
              <p className="text-sm text-stone-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "narocila" && (
        <div className="bg-white rounded-2xl border border-[#E4E0D7] overflow-x-auto" data-testid="admin-orders-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-[#E4E0D7] text-left text-xs font-tech uppercase tracking-wider text-stone-500">
                <th className="px-5 py-3.5">Št. naročila</th>
                <th className="px-5 py-3.5">Datum</th>
                <th className="px-5 py-3.5">Kupec</th>
                <th className="px-5 py-3.5">Postavke</th>
                <th className="px-5 py-3.5 text-right">Znesek</th>
                <th className="px-5 py-3.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-stone-500">Še ni naročil.</td></tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-b border-[#F3EFE6] hover:bg-[#FBF9F5]">
                  <td className="px-5 py-3.5 font-tech font-semibold text-[#B45309]">{o.id}</td>
                  <td className="px-5 py-3.5 text-stone-600">{new Date(o.created_at).toLocaleDateString("sl-SI")}</td>
                  <td className="px-5 py-3.5">{o.customer.ime} {o.customer.priimek}<br /><span className="text-xs text-stone-500">{o.customer.email}</span></td>
                  <td className="px-5 py-3.5 text-stone-600">{o.items.length}</td>
                  <td className="px-5 py-3.5 text-right font-tech font-semibold">{formatEUR(o.total)}</td>
                  <td className="px-5 py-3.5">
                    <select
                      data-testid={`admin-order-status-${o.id}`}
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="h-9 px-3 rounded-lg bg-[#FBF9F5] border border-[#E4E0D7] text-xs font-semibold focus:outline-none"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "izdelki" && (
        <div data-testid="admin-products-section">
          <div className="flex justify-end mb-4">
            <button
              data-testid="admin-add-product-btn"
              onClick={openCreate}
              className="flex items-center gap-2 h-10 px-5 rounded-full bg-[#B45309] text-white text-sm font-semibold hover:bg-[#92400e] transition-colors"
            >
              <Plus className="w-4 h-4" /> Nov izdelek
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-[#E4E0D7] overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-[#E4E0D7] text-left text-xs font-tech uppercase tracking-wider text-stone-500">
                  <th className="px-5 py-3.5">Izdelek</th>
                  <th className="px-5 py-3.5">Kategorija</th>
                  <th className="px-5 py-3.5 text-right">Cena</th>
                  <th className="px-5 py-3.5 text-right">Stara cena</th>
                  <th className="px-5 py-3.5 text-right">Dejanja</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-[#F3EFE6] hover:bg-[#FBF9F5]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                        <span className="font-semibold text-[#1A1D1A] line-clamp-1 max-w-xs">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-stone-600">{catLabel(p.category)}</td>
                    <td className="px-5 py-3 text-right font-tech font-semibold">{formatEUR(p.price)}</td>
                    <td className="px-5 py-3 text-right font-tech text-stone-400">{p.old_price ? formatEUR(p.old_price) : "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        data-testid={`admin-edit-product-${p.id}`}
                        onClick={() => openEdit(p)}
                        className="p-2 text-stone-500 hover:text-[#2D4030] transition-colors"
                        aria-label="Uredi"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`admin-delete-product-${p.id}`}
                        onClick={() => deleteProduct(p)}
                        className="p-2 text-stone-500 hover:text-red-600 transition-colors"
                        aria-label="Izbriši"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "nastavitve" && settingsForm && (
        <div className="max-w-xl bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8" data-testid="admin-settings-form">
          <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-1">Kontaktni podatki</h2>
          <p className="text-sm text-stone-500 mb-6">Ti podatki se prikažejo v zgornji vrstici, nogi in kontaktnem odseku strani.</p>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">Telefonske številke (oddelek + številka)</label>
              <div className="space-y-2">
                {phoneRows.map((line, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2">
                    <input
                      data-testid={`settings-phone-dept-${i}`}
                      value={line.oddelek}
                      placeholder="Oddelek (npr. Prodaja)"
                      onChange={(e) => updatePhone(i, "oddelek", e.target.value)}
                      className={inputCls}
                    />
                    <input
                      data-testid={`settings-phone-num-${i}`}
                      value={line.stevilka}
                      placeholder="Številka (npr. 051 384 644)"
                      onChange={(e) => updatePhone(i, "stevilka", e.target.value)}
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
            </div>
            {[
              ["email", "E-pošta"],
              ["naslov", "Ulica in hišna številka"],
              ["posta_kraj", "Pošta in kraj"],
              ["odpiralni_cas", "Odpiralni čas"],
              ["obvestilo", "Obvestilo v zgornji vrstici"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">{label}</label>
                <input
                  data-testid={`settings-input-${k}`}
                  value={settingsForm[k] || ""}
                  onChange={(e) => setSettingsForm({ ...settingsForm, [k]: e.target.value })}
                  className={inputCls}
                />
              </div>
            ))}
            <button
              data-testid="settings-save-btn"
              onClick={saveSettings}
              disabled={savingSettings}
              className="w-full h-11 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors disabled:opacity-60"
            >
              {savingSettings ? "Shranjujem …" : "Shrani kontaktne podatke"}
            </button>
          </div>
        </div>
      )}

      {tab === "vsebina" && contentForm && (
        <div className="max-w-2xl space-y-6" data-testid="admin-content-form">
          {CONTENT_GROUPS.map((group) => (
            <div key={group.title} className="bg-white rounded-2xl border border-[#E4E0D7] p-6 sm:p-8">
              <h2 className="font-display font-bold text-lg text-[#1A1D1A] mb-1">{group.title}</h2>
              <p className="text-sm text-stone-500 mb-5">{group.desc}</p>
              <div className="space-y-4">
                {group.fields.map(([k, label, kind]) => (
                  <div key={k}>
                    <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">{label}</label>
                    {k.includes("slika") ? (
                      <ImageField value={contentForm[k] || ""} onChange={(v) => setContentForm({ ...contentForm, [k]: v })} testid={`content-input-${k}`} />
                    ) : kind === "input" ? (
                      <input
                        data-testid={`content-input-${k}`}
                        value={contentForm[k] || ""}
                        onChange={(e) => setContentForm({ ...contentForm, [k]: e.target.value })}
                        className={inputCls}
                      />
                    ) : (
                      <textarea
                        data-testid={`content-input-${k}`}
                        rows={kind === "textarea-lg" ? 12 : 4}
                        value={contentForm[k] || ""}
                        onChange={(e) => setContentForm({ ...contentForm, [k]: e.target.value })}
                        className={`${inputCls} resize-y h-auto py-3`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button
            data-testid="content-save-btn"
            onClick={saveContent}
            disabled={savingContent}
            className="h-11 px-8 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors disabled:opacity-60"
          >
            {savingContent ? "Shranjujem …" : "Shrani vsebino"}
          </button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#FBF9F5]">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Uredi izdelek" : "Nov izdelek"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProduct} className="space-y-4 mt-2">
            <Field label="Ime izdelka *" testid="product-form-name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name-input" className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kategorija *" testid="product-form-category">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-form-category-select" className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Enota" testid="product-form-unit">
                <input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="product-form-unit-input" className={inputCls} />
              </Field>
              <Field label="Cena (€) *" testid="product-form-price">
                <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price-input" className={inputCls} />
                {(() => {
                  const info = getSheetInfo({ category: form.category, dimensions: form.dimensions, price: Number(form.price) || 0 });
                  if (!info || !Number(form.price)) return null;
                  return (
                    <p className="text-xs text-stone-500 mt-1.5 font-tech" data-testid="product-form-m2-hint">
                      {info.mode === "tm"
                        ? `= ${formatEUR(info.unitPrice)} / tekoči meter (${info.lengthM.toFixed(2)} tm na ploščo)`
                        : `= ${formatEUR(info.unitPrice)} / m² (${info.areaM2.toFixed(2)} m² na ploščo)`}
                    </p>
                  );
                })()}
              </Field>
              <Field label="Stara cena (€)" testid="product-form-old-price">
                <input type="number" step="0.01" min="0" value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })} data-testid="product-form-old-price-input" className={inputCls} placeholder="prazno = ni akcije" />
              </Field>
              <Field label="Dekor" testid="product-form-decor">
                <input value={form.decor} onChange={(e) => setForm({ ...form, decor: e.target.value })} data-testid="product-form-decor-input" className={inputCls} placeholder="Hrast, Oreh …" />
              </Field>
              <Field label="Debelina (mm)" testid="product-form-thickness">
                <input type="number" min="0" value={form.thickness} onChange={(e) => setForm({ ...form, thickness: e.target.value })} data-testid="product-form-thickness-input" className={inputCls} />
              </Field>
            </div>
            <Field label="Dimenzije" testid="product-form-dimensions">
              <input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} data-testid="product-form-dimensions-input" className={inputCls} placeholder="2800 x 2070 x 18 mm" />
            </Field>
            <Field label="Slika izdelka" testid="product-form-image">
              <ImageField value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="product-form-image-input" />
            </Field>
            <Field label="Oznaka (badge)" testid="product-form-badge">
              <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="product-form-badge-input" className={inputCls} placeholder="Akcija, Priljubljeno …" />
            </Field>
            <Field label="Opis" testid="product-form-desc">
              <textarea rows={3} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} data-testid="product-form-desc-input" className={`${inputCls} resize-none h-auto py-3`} />
            </Field>
            <button
              data-testid="product-form-save-btn"
              type="submit"
              disabled={saving}
              className="w-full h-11 rounded-full bg-[#2D4030] text-[#FBF9F5] font-semibold hover:bg-[#1E2C20] transition-colors disabled:opacity-60"
            >
              {saving ? "Shranjujem …" : editing ? "Shrani spremembe" : "Dodaj izdelek"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const inputCls = "w-full h-11 px-4 rounded-lg bg-white border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-xs font-tech uppercase tracking-wider text-stone-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
